import { AnimatePresence, motion } from 'framer-motion'
import NextLink from 'next/link'
import { useState } from 'react'

import { easings } from 'lib/easings'
import { useTranslation } from 'lib/i18n'
import { cn } from 'lib/utils/tailwind'

import { Button } from './Button'
import { Icon } from './Icon'

export const BREADCRUMBS_MIN_LEVEL = 2

export type Breadcrumb = {
  breadcrumb: string
  href: string
}

type BreadcrumbsProps = {
  breadcrumbs: Breadcrumb[]
}

export const Breadcrumbs = ({ breadcrumbs }: BreadcrumbsProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const { i18n } = useTranslation()

  return breadcrumbs ? (
    <div className="w-full relative">
      <AnimatePresence>
        <motion.div
          key="backdrop"
          className={cn(
            'fixed top-0 bg-foreground-dark/80 z-[500] h-full w-full',
            !isDrawerOpen && 'pointer-events-none'
          )}
          initial={{ opacity: 0 }}
          animate={{ opacity: isDrawerOpen ? 1 : 0 }}
          exit={{ opacity: 0 }}
          onClick={() => setIsDrawerOpen(false)}
        />
        <motion.div
          key="breadcrumbs-drawer"
          className={cn(
            'fixed bottom-0 w-full h-[calc(100%-56px)]',
            'flex flex-col justify-between rounded-t-5 overflow-y-scroll bg-background z-[500]',
            !isDrawerOpen && 'pointer-events-none'
          )}
          initial={{ y: '100%' }}
          animate={{
            y: isDrawerOpen ? 0 : '100%',
            transition: {
              ease: easings['emphasized-decelerate'],
              duration: 0.4,
            },
          }}
          exit={{
            y: '100%',
            transition: {
              ease: easings['emphasized-accelerate'],
              duration: 0.2,
            },
          }}
        >
          <div className="px-5 py-4 flex flex-col gap-8">
            <div
              className="flex items-center gap-2 w-fit hover:cursor-pointer"
              onClick={() => setIsDrawerOpen(false)}
              role="button"
            >
              <Icon
                size={20}
                icon="double-arrow-right"
                className="opacity-60"
              />
              <p className="text-sixteen font-regular">
                {i18n('hideBreadcrumbs')}
              </p>
            </div>
            {breadcrumbs?.map(({ breadcrumb, href }, index) => (
              <div
                className="flex items-center gap-2"
                key={`drawer-${href}-${index}`}
              >
                <Icon size={20} icon="return" />
                <NextLink
                  href={href}
                  className="text-foreground text-eighteen font-regular capitalize whitespace-nowrap hover:text-foreground/80 focus-visible:text-foreground/80"
                >
                  {breadcrumb === 'Home'
                    ? `TFP ${i18n('homepage')}`
                    : breadcrumb}
                </NextLink>
              </div>
            ))}
          </div>
          <div className="p-2 border-y border-foreground/10">
            <Button
              variant="tertiary"
              className="w-full"
              size="sm"
              onClick={() => setIsDrawerOpen(false)}
            >
              {i18n('done')}
            </Button>
          </div>
        </motion.div>
      </AnimatePresence>

      <nav
        aria-label="breadcrumb"
        className="text-foreground bg-background overflow-hidden px-5 lg:px-20 w-full mx-auto max-w-screen-desktop"
      >
        <ul className="hidden group md:flex flex-wrap gap-y-4 h-15 items-center">
          {breadcrumbs?.map(({ breadcrumb, href }, index) => {
            const isLast = breadcrumbs.length - 1 === index

            return (
              <li
                key={`nav-${href}-${index}`}
                className="flex items-center justify-center gap-1 mr-1 last:mr-0"
              >
                {isLast ? (
                  <span className="text-foreground text-sixteen font-regular whitespace-nowrap">
                    {breadcrumb}
                  </span>
                ) : (
                  <NextLink
                    href={href}
                    className="text-foreground/60 text-sixteen font-regular capitalize whitespace-nowrap hover:text-foreground/80 focus-visible:text-foreground/80"
                  >
                    {breadcrumb}
                  </NextLink>
                )}

                {!isLast && (
                  <Icon size={20} icon="chevron-right" className="opacity-70" />
                )}
              </li>
            )
          })}
        </ul>

        {breadcrumbs?.length > BREADCRUMBS_MIN_LEVEL && (
          <div className="md:hidden text-foreground text-sm font-regular flex flex-col gap-2 mb-2">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="h-6 w-6 min-w-6 !rounded-[6.8px] m-0 px-1 pb-1 pt-1.5 flex items-center justify-center"
                onClick={() => setIsDrawerOpen(!isDrawerOpen)}
                aria-label="Open breadcrumbs menu"
                data-testid="breadcrumbs-drawer-button"
                data-open={isDrawerOpen}
              >
                <Icon icon="double-return" size={16} />
              </Button>
              <NextLink
                href={breadcrumbs[breadcrumbs.length - 1].href}
                className="text-foreground text-eighteen font-regular capitalize whitespace-nowrap hover:text-foreground/80 focus-visible:text-foreground/80"
              >
                {breadcrumbs[breadcrumbs.length - 1].breadcrumb}
              </NextLink>
            </div>
          </div>
        )}
      </nav>
    </div>
  ) : (
    <></>
  )
}
